import React from 'react';
import { Ticket, ChevronRight, Lock, AlertTriangle } from 'lucide-react';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fieldTicketStatusLabel, ticketStatusGroup, type TicketStatusGroup } from '../../lib/ticket-status';
import {
  canWriteFieldTicket,
  isOnJobCrew,
  ticketWriteRefusalReason,
  type JobCrewMemberShape,
} from '../../lib/field-ticket-write';

/**
 * The tickets already on this job, on the job screen.
 *
 * ─── The bug this exists to close ────────────────────────────────────────────
 *
 * FieldJobDetail offered exactly one ticket control — "Create Ticket" — and
 * tapping it navigated to /field/tickets with `createFromJob` in router state,
 * where FieldTicketsScreen fires the create mutation from a mount effect. No
 * confirmation, no check for an existing ticket: a hand doing the obvious thing
 * had a second ticket before the screen finished painting. The job's own
 * tickets were never shown on the job screen at all — they were reachable only
 * through the bottom-nav Tickets tab, mixed in with every other job's.
 *
 * It is not hypothetical. On 2026-08-27 the production database had four TNT
 * jobs carrying more than one ticket, three of them from a single author.
 *
 * ─── Where the rows come from ───────────────────────────────────────────────
 *
 * The caller passes tickets already filtered to this job out of the shared
 * ['field-my-tickets'] cache — the same unfiltered GET /employee/my-tickets
 * that FieldTicketsScreen loads. Checked against the route's SQL rather than
 * assumed: it narrows only on `?status=`, it takes no date or job parameter,
 * and its visibility clause admits any member of the job's crew.
 * So every ticket on this job that this hand is allowed to see is in that list,
 * whatever day it is for — which is the whole point when a wash day and a run
 * day belong on one ticket.
 *
 * ─── Seeing is wider than writing, and the gap is a 404 ─────────────────────
 *
 * A hand on the crew SEES every ticket on the job but may only WRITE their own
 * (or one where they are the job's designated `ticket_writer`, which is nobody
 * today). The refusal is a 404 reading "Ticket not found or not yours", about a
 * ticket the hand is looking straight at.
 *
 * So a row this hand cannot write is not a button. It shows the same three
 * facts — number, date, status — and, in place of the chevron, says who has it
 * and what to do about it. Fixing the underlying access rule is an API change
 * and is not attempted here; what this closes is that the screen no longer
 * offers a door that answers "this does not exist".
 *
 * ─── The empty state can lie, for one class of viewer ───────────────────────
 *
 * "No ticket on this job yet" is only true if this list is the whole list, and
 * it is not always. `my-tickets` admits a ticket when the user submitted it, is
 * an assigned line-item tech on it, or is on the job's crew. The job screen
 * behind it is narrower for field workers only: `enforceJobCrewMembership`
 * 404s a `field_worker` who is not on the crew, and returns early for every
 * other role. So an admin, dispatcher, supervisor or office_staff can open a
 * field job screen for a job they are not crewed on — and would be shown a
 * confident "no tickets" over a job that has several.
 *
 * That is the one shape this card must never render, because it is the exact
 * belief that produces the duplicate. When the viewer is not on the crew the
 * card says what it can actually see instead of asserting a negative.
 */

const GROUP_BADGE: Record<TicketStatusGroup, string> = {
  draft: 'bg-fieldiq-muted/20 text-fieldiq-text',
  submitted: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  invoiced: 'bg-purple-500/20 text-purple-400',
  archived: 'bg-fieldiq-muted/20 text-fieldiq-muted',
};

export interface JobTicketRow {
  id: string;
  ticket_number?: string | null;
  ticketNumber?: string | null;
  work_date?: string | null;
  status?: string | null;
  submitted_by?: string | null;
  submittedBy?: string | null;
  _pendingSync?: boolean;
}

interface Props {
  tickets: JobTicketRow[];
  crew: JobCrewMemberShape[];
  currentUserId: string | null;
  /** True while the ticket list is still being fetched for the first time. */
  isLoading?: boolean;
  /** True when the list came back from IndexedDB rather than the server. */
  isOffline?: boolean;
  onOpen: (ticketId: string) => void;
}

export default function JobTicketsCard({
  tickets,
  crew,
  currentUserId,
  isLoading,
  isOffline,
  onOpen,
}: Props) {
  const onCrew = isOnJobCrew(crew, currentUserId);

  return (
    <div className="bg-fieldiq-card rounded-xl p-4" data-testid="job-tickets-card">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <Ticket size={18} className="text-fieldiq-accent" /> Tickets ({tickets.length})
      </h3>

      {isLoading ? (
        <p className="text-sm text-fieldiq-muted">Checking this job's tickets…</p>
      ) : tickets.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">
          {onCrew
            ? 'No ticket on this job yet.'
            : "No ticket on this job that you started or were assigned to."}
        </p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const number = t.ticket_number ?? t.ticketNumber ?? null;
            // An absent value renders as an em dash. A ticket with no
            // work_date yet is a real state — a draft created from the job
            // before anyone typed a day on it — and "today" would be a
            // plausible wrong answer nobody would question.
            const day = formatCalendarDate(t.work_date, { month: 'short', day: 'numeric' });
            const writable = canWriteFieldTicket(t, currentUserId, crew);
            const refusal = ticketWriteRefusalReason(t, currentUserId, crew);
            const group = ticketStatusGroup(t.status);

            const facts = (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">
                    {number ?? '—'}
                    {t._pendingSync && (
                      <span className="ml-1.5 text-[10px] font-normal text-orange-400">(offline)</span>
                    )}
                  </p>
                  <p className="text-xs text-fieldiq-muted">{day ?? '—'}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${GROUP_BADGE[group]}`}>
                    {fieldTicketStatusLabel(t.status)}
                  </span>
                  {writable
                    ? <ChevronRight size={16} className="text-fieldiq-muted" />
                    : <Lock size={14} className="text-fieldiq-muted" aria-hidden="true" />}
                </div>
              </div>
            );

            return writable ? (
              <button
                key={t.id}
                onClick={() => onOpen(t.id)}
                className="w-full text-left bg-fieldiq-dark rounded-lg px-3 py-2.5 active:scale-[0.99] transition-transform"
              >
                {facts}
              </button>
            ) : (
              <div
                key={t.id}
                className="bg-fieldiq-dark rounded-lg px-3 py-2.5 space-y-1.5"
                data-testid={`job-ticket-readonly-${t.id}`}
              >
                {facts}
                <p className="text-xs text-fieldiq-muted flex items-start gap-1.5">
                  <Lock size={11} className="flex-shrink-0 mt-0.5" />
                  <span>{refusal}</span>
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Not on the crew: this list is a subset and cannot answer "does this
          job have a ticket". Rendered whether or not the list is empty — a
          partial list of two is as misleading as a partial list of none. */}
      {!onCrew && !isLoading && (
        <p className="mt-3 text-xs text-amber-400 flex items-start gap-1.5">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
          <span>
            You're not on this job's crew, so this shows only tickets you started
            or were assigned to — not every ticket on the job.
          </span>
        </p>
      )}

      {/* A stale cache is the one case where "no ticket on this job" can be a
          lie, and it is the case a hand is most likely to hit — in a dead zone,
          about to decide whether to make one. Say which list this is. */}
      {isOffline && !isLoading && (
        <p className="mt-3 text-xs text-amber-400 flex items-start gap-1.5">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
          <span>You're offline — this is the last list your phone downloaded.</span>
        </p>
      )}
    </div>
  );
}
